import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { SlideshowSelectEvent } from '@theme/events';
import {
  applyStageAspect,
  clampCarouselIndex,
  clearStageAspect,
  resolveTriggerOpenState,
} from '@theme/product-video-popup';

export {
  DEFAULT_ASPECT_RATIO,
  applyStageAspect,
  clampCarouselIndex,
  clearStageAspect,
  getCardOpenIndex,
  isProductVideoPayload,
  parseProductVideosCatalog,
  readCatalogFromScriptId,
  readTriggerVideoCatalog,
  resolveTriggerOpenState,
} from '@theme/product-video-popup';

/**
 * @typedef {import('@theme/product-video-popup').ProductVideoPayload} ProductVideoPayload
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} carouselHost
 */

/**
 * @param {ProductVideoPayload} payload
 * @param {HTMLElement} stage
 * @returns {HTMLVideoElement | HTMLIFrameElement | null}
 */
export function mountPlayer(payload, stage) {
  stage.innerHTML = '';

  if (payload.mediaType === 'video' && payload.sources?.length) {
    const video = document.createElement('video');
    video.setAttribute('controls', '');
    video.setAttribute('playsinline', '');
    video.playsInline = true;
    video.className = 'product-video-modal__video';
    if (payload.alt) {
      video.setAttribute('title', payload.alt);
    }
    for (const s of payload.sources) {
      const source = document.createElement('source');
      source.src = s.url;
      if (s.mimeType) source.type = s.mimeType;
      video.appendChild(source);
    }
    stage.appendChild(video);
    return video;
  }

  if (payload.mediaType === 'external_video' && payload.host && payload.externalId) {
    const iframe = document.createElement('iframe');
    iframe.className = 'product-video-modal__iframe';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.setAttribute('title', payload.alt || 'Video');
    const host = String(payload.host).toLowerCase();
    if (host === 'youtube') {
      const id = encodeURIComponent(payload.externalId);
      iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1&mute=0&playsinline=1&rel=0`;
    } else if (host === 'vimeo') {
      const id = encodeURIComponent(payload.externalId);
      iframe.src = `https://player.vimeo.com/video/${id}?autoplay=1&muted=0`;
    } else {
      return null;
    }
    stage.appendChild(iframe);
    return iframe;
  }

  return null;
}

/**
 * @param {HTMLElement} stage
 */
export function teardownStage(stage) {
  const video = stage.querySelector('video');
  if (video) {
    video.pause();
    video.removeAttribute('src');
    for (const s of video.querySelectorAll('source')) {
      s.removeAttribute('src');
    }
  }
  const iframe = stage.querySelector('iframe');
  if (iframe) {
    iframe.removeAttribute('src');
  }
  stage.innerHTML = '';
  clearStageAspect(stage);
}

/**
 * @param {number} count
 * @returns {HTMLElement}
 */
function buildSlideshowControls(count) {
  const controls = document.createElement('slideshow-controls');
  controls.setAttribute('pagination-position', 'center');
  controls.setAttribute('scroll-mode', 'horizontal');
  controls.setAttribute('controls-on-media', '');
  controls.setAttribute('ref', 'slideshowControls');

  const list = document.createElement('ol');
  list.className = 'slideshow-controls__dots';

  for (let i = 0; i < count; i += 1) {
    const item = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'slideshow-control button button-unstyled';
    button.style.animationTimeline = `--slide-${i}`;
    button.setAttribute('aria-label', `Slide ${i + 1} of ${count}`);
    button.setAttribute('on:click', `/select/${i}`);
    button.setAttribute('ref', 'dots[]');
    if (i === 0) {
      button.setAttribute('aria-selected', 'true');
    }
    button.textContent = String(i + 1);
    item.appendChild(button);
    list.appendChild(item);
  }

  controls.appendChild(list);
  return controls;
}

/**
 * @param {ProductVideoPayload[]} videos
 * @param {number} initialIndex
 * @returns {HTMLElement}
 */
export function buildModalSlideshow(videos, initialIndex) {
  const slideshow = document.createElement('slideshow-component');
  slideshow.className = 'product-video-modal__slideshow';
  slideshow.setAttribute('initial-slide', String(initialIndex));

  const timelineScope = videos.map((_, index) => `--slide-${index}`).join(', ');
  slideshow.style.setProperty('--slideshow-timeline', timelineScope);

  const container = document.createElement('slideshow-container');
  container.setAttribute('ref', 'slideshowContainer');

  const scroller = document.createElement('slideshow-slides');
  scroller.setAttribute('tabindex', '-1');
  scroller.setAttribute('ref', 'scroller');

  videos.forEach((video, index) => {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('slide-id', String(video.id));
    slide.setAttribute('aria-hidden', index === initialIndex ? 'false' : 'true');
    slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);

    const stage = document.createElement('div');
    stage.className = 'product-video-modal__slide-stage';
    stage.dataset.slideIndex = String(index);
    slide.appendChild(stage);
    scroller.appendChild(slide);
  });

  container.appendChild(scroller);
  slideshow.appendChild(container);

  if (videos.length > 1) {
    slideshow.appendChild(buildSlideshowControls(videos.length));
  }

  return slideshow;
}

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLElement} stage
 */
function wireHostedVideoPlayback(video, stage) {
  const syncAspectFromIntrinsic = () => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w > 0 && h > 0) {
      stage.style.setProperty('--product-video-aspect', String(w / h));
    }
  };
  video.addEventListener('loadedmetadata', syncAspectFromIntrinsic);
  if (video.readyState >= 1) {
    syncAspectFromIntrinsic();
  }

  const play = () => {
    requestAnimationFrame(() => {
      video.play().catch(() => {});
    });
  };
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    video.removeAttribute('autoplay');
  } else {
    play();
  }
}

/**
 * @extends {DialogComponent<Refs>}
 */
class ProductVideoModalComponent extends DialogComponent {
  requiredRefs = ['dialog', 'carouselHost'];

  /** @type {ProductVideoPayload[]} */
  #videos = [];
  /** @type {HTMLElement | null} */
  #slideshow = null;
  /** @type {number} */
  #activeIndex = -1;
  /** @type {((event: SlideshowSelectEvent) => void) | null} */
  #onSelect = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, () => {
      this.#teardownCarousel();
    });
  }

  /**
   * @param {Event} event
   */
  openFromTrigger = (event) => {
    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-product-video-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    const { videos, openIndex } = resolveTriggerOpenState(trigger.dataset, trigger);
    if (!videos.length || openIndex < 0) return;

    this.#openCatalog(videos, openIndex);
  };

  /**
   * @param {HTMLElement} host
   * @param {number} index
   */
  #activateSlide(host, index) {
    if (!this.#slideshow || index < 0 || index >= this.#videos.length) return;

    const slides = this.#slideshow.querySelectorAll('slideshow-slide');
    const slide = slides[index];
    if (!(slide instanceof HTMLElement)) return;

    const stage = slide.querySelector('.product-video-modal__slide-stage');
    if (!(stage instanceof HTMLElement)) return;

    if (this.#activeIndex === index && stage.querySelector('video, iframe')) {
      return;
    }

    if (this.#activeIndex >= 0 && this.#activeIndex !== index) {
      const previousStage = slides[this.#activeIndex]?.querySelector('.product-video-modal__slide-stage');
      if (previousStage instanceof HTMLElement) {
        teardownStage(previousStage);
      }
    }

    const payload = this.#videos[index];
    applyStageAspect(stage, payload);

    const mounted = mountPlayer(payload, stage);
    if (!mounted) {
      teardownStage(stage);
      return;
    }

    this.#activeIndex = index;

    if (mounted instanceof HTMLVideoElement) {
      wireHostedVideoPlayback(mounted, stage);
    }
  }

  #teardownCarousel() {
    const host = this.refs.carouselHost;
    if (!(host instanceof HTMLElement)) return;

    if (this.#slideshow && this.#onSelect) {
      this.#slideshow.removeEventListener(SlideshowSelectEvent.eventName, this.#onSelect);
    }
    if (this.#slideshow) {
      for (const stage of this.#slideshow.querySelectorAll('.product-video-modal__slide-stage')) {
        if (stage instanceof HTMLElement) {
          teardownStage(stage);
        }
      }
      this.#slideshow.remove();
    }

    this.#slideshow = null;
    this.#onSelect = null;
    this.#videos = [];
    this.#activeIndex = -1;
    host.replaceChildren();
  }

  /**
   * @param {ProductVideoPayload[]} videos
   * @param {number} openIndex
   */
  #openCatalog(videos, openIndex) {
    const host = this.refs.carouselHost;
    if (!(host instanceof HTMLElement)) return;

    this.#teardownCarousel();
    this.#videos = videos;
    const initialIndex = clampCarouselIndex(openIndex, videos.length);
    if (initialIndex < 0) return;

    if (videos.length === 1) {
      const stage = document.createElement('div');
      stage.className = 'product-video-modal__slide-stage';
      host.appendChild(stage);
      applyStageAspect(stage, videos[0]);
      const mounted = mountPlayer(videos[0], stage);
      if (!mounted) {
        teardownStage(stage);
        host.replaceChildren();
        return;
      }
      if (mounted instanceof HTMLVideoElement) {
        wireHostedVideoPlayback(mounted, stage);
      }
      this.showDialog();
      return;
    }

    this.#slideshow = buildModalSlideshow(videos, initialIndex);
    host.appendChild(this.#slideshow);

    this.#onSelect = (event) => {
      if (!(event instanceof SlideshowSelectEvent)) return;
      this.#activateSlide(host, event.detail.index);
    };
    this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#onSelect);

    // Mount player and establish stage dimensions before opening — avoids zero-size fit-content dialog.
    this.#activateSlide(host, initialIndex);
    this.showDialog();
  }
}

if (!customElements.get('product-video-modal-component')) {
  customElements.define('product-video-modal-component', ProductVideoModalComponent);
}
